/**
 * One read of `dealbid-core`, with the three states a desk panel has to render.
 *
 * Canon `docs/TESTING.md` § UI component/integration treats loading, empty, error and data
 * as separately failable behaviour, so they are modelled explicitly here rather than being
 * inferred from `data === undefined`. Empty is not a state of this hook: an empty deal list
 * and an empty credit ledger mean different things to a founder, so each panel writes its
 * own empty copy over ready data.
 *
 * A ready state also records **when** the response arrived. The bidding countdown is
 * measured against core's clock (canon § 8), and the offset between the two clocks is only
 * computable if the local instant of arrival is kept beside the server time that came with
 * it.
 *
 * Two ways of re-reading, because they answer to different people:
 *
 *   - `reload` is a **retry**, something the seller pressed. It returns to the loading
 *     state so the retry is visible.
 *   - `refresh` is a **silent re-read**, used by polling and after an accepted bid. It
 *     leaves the last good data on screen and replaces it only when the new answer
 *     arrives — a panel that blanked every minute would be worse than one that updates in
 *     place, and a failed background read must not turn a working panel into an error.
 *
 * Polling covers the auction panel alone: canon `AGENTS.md` § 5 allows polling for auction
 * UI in V0 and rules out WebSockets. A review status and a ledger balance change when a
 * person at Dealbid acts, not on a timer, so those panels pass no interval.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../api/client";

export type Loader<T> = () => Promise<T>;

export type ResourceState<T> =
  | { status: "loading" }
  | { status: "ready"; data: T; receivedAtMs: number }
  | { status: "error"; error: ApiError };

export interface Resource<T> {
  state: ResourceState<T>;
  /** Retry after a failure. Returns to the loading state, so the retry is visible. */
  reload: () => void;
  /** Re-read without disturbing what is on screen, and without surfacing a failure. */
  refresh: () => void;
}

function asApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  // A thrown non-ApiError is a bug in this app rather than an answer from core. It is
  // wrapped rather than re-thrown so a panel fails visibly instead of taking down the desk,
  // and deliberately carries no detail from the original: nothing here may surface a URL.
  return new ApiError("This part of your desk didn't load.", { status: null });
}

export function useApiResource<T>(
  load: Loader<T>,
  options: { refreshMs?: number | undefined } = {},
): Resource<T> {
  const { refreshMs } = options;
  const [state, setState] = useState<ResourceState<T>>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  // `live` gates every background write. An unmounted panel must not set state, and a
  // silent re-read has no visible request for the seller to cancel.
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const reload = useCallback(() => {
    // The loading state is entered here, in the event handler, rather than inside the
    // effect: a retry is something the seller did, and the effect's job is only to run the
    // request that follows from it.
    setState({ status: "loading" });
    setAttempt((value) => value + 1);
  }, []);

  // `load` changes identity whenever its caller re-creates it, so the request is read from
  // a ref: a silent refresh always runs the current loader without making `refresh` itself
  // a new function on every render.
  const loader = useRef(load);
  useEffect(() => {
    loader.current = load;
  }, [load]);

  const refresh = useCallback(() => {
    void loader.current().then(
      (data) => {
        if (live.current) setState({ status: "ready", data, receivedAtMs: Date.now() });
      },
      () => {
        // A background read that fails leaves the last good data on screen. The seller did
        // not ask for this request, so a transient blip must not replace a working panel
        // with an error; the next tick, or their next action, tries again.
      },
    );
  }, []);

  useEffect(() => {
    let cancelled = false;

    void load().then(
      (data) => {
        if (!cancelled) setState({ status: "ready", data, receivedAtMs: Date.now() });
      },
      (error: unknown) => {
        // A cancelled effect is an unmount, or React 19's StrictMode running effects twice
        // in development. Either way its result must not replace a good render.
        if (!cancelled) setState({ status: "error", error: asApiError(error) });
      },
    );

    return () => {
      cancelled = true;
    };
  }, [load, attempt]);

  useEffect(() => {
    if (refreshMs === undefined) return undefined;

    const timer = setInterval(() => {
      // A hidden tab costs the seller battery and core requests for an auction nobody is
      // looking at. The next visible tick catches up.
      if (document.visibilityState === "hidden") return;
      refresh();
    }, refreshMs);

    return () => {
      clearInterval(timer);
    };
  }, [refresh, refreshMs]);

  return { state, reload, refresh };
}
